import type { BillingTimeRange, ChartParams, TimeGrouping } from '~/common/charts/types';
import type { OrganizationDetail } from '~/common/types';
import type { BillingSummaryResponse } from '~/pages/billingSummary/store';
import type { OrgSummaryResponse } from '~/pages/orgSummary/store';
import { getAxiosClient } from './common';
import { sortMembers } from './organizations';

const axios = getAxiosClient();

export async function getOrganization(organizationId: string): Promise<OrganizationDetail> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}`,
  });

  return {
    ...data,
    members: sortMembers(data.members),
  };
}

export async function addFile(orgId: string, file: any) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/organizations/${orgId}/files`,
    data: file,
  });
  return data;
}

export async function addMembers(
  organizationId: string,
  memberData: {
    email: string;
    role: string;
  }[],
  projectId?: string,
) {
  const { data } = await axios({
    method: 'POST',
    url: `/v1/organizations/${organizationId}/members`,
    data: { memberData, projectId },
  });
  return data;
}

export async function getOrgSummary(organizationId: string, params: ChartParams = {}): Promise<OrgSummaryResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}/orgSummary`,
    params: {
      groupBy: params.groupBy,
      timeRange: params.timeRange,
    },
  });

  return data;
}

export async function getBillingSummary(
  organizationId: string,
  params: { timeRange: BillingTimeRange; groupBy: TimeGrouping } = {
    timeRange: 'all',
    groupBy: 'month',
  },
): Promise<BillingSummaryResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v1/organizations/${organizationId}/billingSummary`,
    params: {
      groupBy: params.groupBy,
      timeRange: params.timeRange,
    },
  });

  return data;
}

export async function printBillingSummary(
  organizationId: string,
  dateRange: string,
  type: string,
  ccEmails?: string[],
) {
  await axios({
    method: 'POST',
    url: `/v1/organizations/${organizationId}/printBillingSummary`,
    data: { type, dateRange, ccEmails },
  });
}

export async function setTestEnvOverride(organizationId: string, useTestEnv: 'UAT' | null) {
  await axios({
    method: 'POST',
    url: `/v1/organizations/${organizationId}/useTestEnv`,
    data: { useTestEnv },
  });
}
